import React from "react"

import {
  getExperimentStrings,
  getParentLinks,
  getRunDetailValue,
  RUN_DETAIL_FIELDS,
} from "../../../tables/utils"
import { highlightTextModel } from "../../../tables/highlight"

export const getHighlightedSpans = (field, highlight_words = [], metadata = {}, filters = []) => {
  const [key, label, val] = field
  const linkOuts = getParentLinks(key, val)
  const filterSpecificSearch = metadata?.[key] ? filters.find((filter) => filter.tag === key) : null
  const highlightArray = highlightTextModel(
    val,
    filterSpecificSearch?.value ? [filterSpecificSearch.value, ...highlight_words] : highlight_words,
  )

  const hasHighlight = highlightArray.some((f) => f.highlight)

  return [
    hasHighlight,
    [label, <HighlightedValueWithLinks value={val} linkOuts={linkOuts} highlightArray={highlightArray} />],
  ]
}

const HighlightedValueWithLinks = ({ value, linkOuts, highlightArray }) => {
  const linkMap = Object.fromEntries(linkOuts)

  // Regex for links
  const keys = linkOuts.map(([key]) => key).map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
  const linkRegex = new RegExp(`(${keys.join("|")})`, "g")

  // Create a map of positions that should be highlighted
  const highlightPositions = new Set()
  let pos = 0
  highlightArray.forEach((piece) => {
    if (piece.highlight) {
      for (let i = 0; i < piece.text.length; i++) {
        highlightPositions.add(pos + i)
      }
    }
    pos += piece.text.length
  })

  // Split value by links and track global position
  const parts = value.split(linkRegex)
  let globalPos = 0

  return (
    <span>
      {parts.map((part, index) => {
        if (!part) return null

        const href = linkMap[part]
        const partStart = globalPos
        globalPos = globalPos + part.length

        if (href) {
          // It's a link - highlight individual characters inside it
          return (
            <a key={index} href={href} target="_blank" rel="noopener noreferrer" className={"parent-link"}>
              {Array.from(part).map((char, i) => {
                const shouldHighlight = highlightPositions.has(partStart + i)
                return (
                  <span key={i} style={shouldHighlight ? { backgroundColor: "yellow" } : {}}>
                    {char}
                  </span>
                )
              })}
            </a>
          )
        } else {
          // Not a link - highlight individual characters
          return (
            <span key={index}>
              {Array.from(part).map((char, i) => {
                const shouldHighlight = highlightPositions.has(partStart + i)
                return (
                  <span key={i} style={shouldHighlight ? { backgroundColor: "yellow" } : {}}>
                    {char}
                  </span>
                )
              })}
            </span>
          )
        }
      })}
    </span>
  )
}

export const ExperimentDiv = ({ run = {} }) => {
  const { experiments = [], highlight_words = [] } = run
  const exps = getExperimentStrings(experiments)
  if (exps.length === 0) {
    return <>-</>
  }
  return (
    <>
      {exps.map((exp, i) => {
        const highlightArray = highlightTextModel(exp, highlight_words)
        return (
          <div key={`${run.name}${exp}${i}`} style={{ marginBottom: "5px" }}>
            <HighlightedValueWithLinks value={exp} linkOuts={[]} highlightArray={highlightArray} />
          </div>
        )
      })}
    </>
  )
}

export const RunParentDiv = ({ run = {} }) => {
  const { highlight_words = [] } = run
  const finalParentFields = [
    "externalRuns",
    "externalSamples",
    "bioCatalystParents",
    "runParents",
    "external_runs",
    "external_samples",
    "biocatalyst_parents",
    "run_parents",
  ].filter((field) => run[field] && run[field].length > 0)
  if (finalParentFields.length === 0) {
    return <>-</>
  }
  return (
    <>
      {finalParentFields.map((field, i) => {
        const detail_type = RUN_DETAIL_FIELDS.find((f) => f.key === field)
        const value = getRunDetailValue(run, detail_type)
        const [_, [label, final]] = getHighlightedSpans(
          [detail_type.key, detail_type.label, value],
          highlight_words,
        )

        return (
          <div key={`${run.name}${label}${i}`} style={{ marginBottom: "5px" }}>
            <label style={{ margin: "0" }}>
              <b>{label}:</b>
            </label>
            &nbsp;
            {final}
          </div>
        )
      })}
    </>
  )
}
